import { useState, useEffect } from 'react';

export const useNcaafCalendar = (selectedSport) => {
  const [calendarData, setCalendarData] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCalendar = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/games/ncaaf/schedule/calendar');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const weeks = Array.isArray(data.weeks) ? data.weeks : [];
        setCalendarData({ ...data, weeks });
        setCurrentWeek(data.currentWeek);

        // Pick the week whose date range contains today. Same shape as the
        // NFL calendar: iterate week.startDate in order and match today into
        // the interval [startDate, nextWeek.startDate). If nothing matches
        // (pre-season or post-bowls gap), snap to the nearest end of the
        // visible window instead of a hardcoded value.
        const now = new Date();
        let weekToSelect = null;
        for (let i = 0; i < weeks.length; i++) {
          const w = weeks[i];
          if (!w.startDate) continue;
          const start = new Date(w.startDate);
          const nextStart = i < weeks.length - 1 && weeks[i + 1].startDate
            ? new Date(weeks[i + 1].startDate)
            : null;
          if (now >= start && (!nextStart || now < nextStart)) {
            weekToSelect = parseInt(w.value);
            break;
          }
        }

        let fallback = null;
        if (weeks.length > 0) {
          const first = weeks[0];
          const last = weeks[weeks.length - 1];
          if (first.startDate && now < new Date(first.startDate)) {
            fallback = parseInt(first.value);
          } else {
            fallback = parseInt(last.value);
          }
        }

        setSelectedWeek(
          weekToSelect ?? (data.currentWeek ? parseInt(data.currentWeek) : fallback)
        );
      } else {
        throw new Error('Failed to fetch calendar data');
      }
    } catch (err) {
      console.error('Error fetching NCAAF calendar:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSport === 'ncaaf') {
      fetchCalendar();
    } else {
      // Reset state when switching away from NCAAF
      setCalendarData(null);
      setCurrentWeek(null);
      setSelectedWeek(null);
      setError(null);
    }
  }, [selectedSport]);

  const getCurrentWeekData = () => {
    if (!calendarData || !currentWeek) return null;
    return calendarData.weeks.find(week => week.value === String(currentWeek));
  };

  const getSelectedWeekData = () => {
    if (!calendarData || !selectedWeek) return null;
    return calendarData.weeks.find(week => week.value === String(selectedWeek));
  };

  return {
    calendarData,
    currentWeek,
    selectedWeek,
    setSelectedWeek,
    getCurrentWeekData,
    getSelectedWeekData,
    isLoading,
    error,
    refetch: fetchCalendar
  };
};